import { useApi } from '@classgo/app-kit';
import { Button, Checkbox, Select } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { listQuery } from '../academic/api';
import { useT } from '../i18n';
import { peopleApi } from '../people/api';
import { useTenant } from '../tenant/tenant-provider';
import { FormShell } from './form-shell';

type Row = { key: number; groupId: string; subjectId: string; isHomeroom: boolean };

/**
 * A teacher's assignments for the active year: one row per group and subject, or the homeroom
 * of a group without a subject. The whole set is replaced on save (`PUT …/assignments`).
 */
export const AssignmentsForm = ({
  teacherId,
  initial,
  onDone,
}: {
  teacherId: string;
  initial: { groupId: string; subjectId: string | null; isHomeroom: boolean }[];
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const { sede } = useTenant();
  const years = useQuery(listQuery(api, 'years'));
  const groups = useQuery(listQuery(api, 'groups', { sedeId: sede.id }));
  const subjects = useQuery(listQuery(api, 'subjects'));
  const activeYear = (years.data?.items ?? []).find((year) => year.status === 'active');
  const [rows, setRows] = useState<Row[]>(
    initial.map((row, index) => ({
      key: index,
      groupId: row.groupId,
      subjectId: row.subjectId ?? '',
      isHomeroom: row.isHomeroom,
    })),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const groupOptions = (groups.data?.items ?? []).filter(
    (group) => !activeYear || group.schoolYearId === activeYear.id,
  );
  const update = (key: number, patch: Partial<Row>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!activeYear) {
      setError(t('student.enroll.noYear'));
      return;
    }
    const incomplete = rows.some((row) => !row.groupId || (!row.isHomeroom && !row.subjectId));
    if (incomplete) {
      setError(t('assignments.incomplete'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await peopleApi(api).putAssignments(teacherId, {
        schoolYearId: activeYear.id,
        assignments: rows.map((row) => ({
          groupId: row.groupId,
          subjectId: row.isHomeroom ? null : row.subjectId,
          isHomeroom: row.isHomeroom,
        })),
      });
      onDone();
    } catch (thrown) {
      setError(thrown instanceof Error ? thrown.message : t('auth.error.UNKNOWN'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormShell error={error} busy={busy} onSubmit={(event) => void submit(event)}>
      <p className="text-sm text-gray-600">
        {activeYear ? t('assignments.year', { year: activeYear.name }) : t('student.enroll.noYear')}
      </p>
      <ul className="space-y-3">
        {rows.map((row) => (
          <li
            key={row.key}
            className="grid gap-2 rounded-control bg-gray-50 p-3 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end"
          >
            <Select
              label={t('assignments.group')}
              value={row.groupId}
              onChange={(event) => update(row.key, { groupId: event.target.value })}
              options={[
                { value: '', label: t('assignments.pick') },
                ...groupOptions.map((group) => ({ value: group.id, label: group.name })),
              ]}
            />
            <Select
              label={t('assignments.subject')}
              value={row.subjectId}
              disabled={row.isHomeroom}
              onChange={(event) => update(row.key, { subjectId: event.target.value })}
              options={[
                {
                  value: '',
                  label: row.isHomeroom ? t('assignments.homeroomOnly') : t('assignments.pick'),
                },
                ...(subjects.data?.items ?? []).map((subject) => ({
                  value: subject.id,
                  label: subject.name,
                })),
              ]}
            />
            <Checkbox
              label={t('assignments.homeroom')}
              checked={row.isHomeroom}
              onChange={(event) =>
                update(row.key, {
                  isHomeroom: event.target.checked,
                  subjectId: event.target.checked ? '' : row.subjectId,
                })
              }
            />
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() =>
                setRows((current) => current.filter((candidate) => candidate.key !== row.key))
              }
            >
              {t('assignments.remove')}
            </Button>
          </li>
        ))}
      </ul>
      <Button
        variant="secondary"
        size="sm"
        type="button"
        onClick={() =>
          setRows((current) => [
            ...current,
            { key: Date.now(), groupId: '', subjectId: '', isHomeroom: false },
          ])
        }
      >
        {t('assignments.add')}
      </Button>
    </FormShell>
  );
};
